<template>
  <div class="bTable">
    <b-card
      no-body
      class="mb-0"
    >
      <div
        class="mom-table-toolbar"
        role="search"
      >
        <div
          v-if="searchable"
          class="mom-table-toolbar__search"
        >
          <b-form-input
            v-model="searchQuery"
            type="search"
            :aria-label="$t('foundation.searchLabel')"
            :placeholder="$t('general.search')"
          />
        </div>
        <div
          v-if="$route.path == '/articles/list'"
          class="mom-table-toolbar__search"
        >
          <b-form-group
            label-for="article-category"
            :label="$t('general.category')"
            label-class="sr-only"
            class="mb-0"
          >
            <v-select-custom
              id="article-category"
              v-model="categoryId"
              :options="rootCategoryOptions"
            />
            <b-form-invalid
              id="input-category-feedback"
              :label="$t('general.category')"
              :is-bootstrap="false"
            />
          </b-form-group>
        </div>
        <div class="mom-table-toolbar__spacer" />
        <div
          v-if="selectList.length"
          class="mom-table-toolbar__selection"
          role="status"
        >
          <span>{{ $t('foundation.selectedCount', { count: selectList.length }) }}</span>
        </div>
        <slot
          name="leftHeader"
          :selectList="selectList"
        >
          <b-button
            v-if="deleteItem && selectList.length"
            variant="outline-danger"
            :disabled="isLoading"
            @click="groupDelete"
          >
            {{ $t("general.delete") }} {{ $t("general.group") }}
            {{ selectList.length ? `(${selectList.length})` : "" }}
          </b-button>
        </slot>
        <slot name="rightHeader" />
        <b-button
          v-if="createRoute"
          variant="primary"
          :to="createRoute"
        >
          {{ createLabel || $t('general.add') }}
        </b-button>
      </div>

      <b-col
        v-if="showSelectAllSwitchButton"
        class="d-flex justify-content-end align-items-center mb-2"
        style="margin-top:-28px"
      >
        <h5 class="mr-1 mt-1">
          انتخاب همه
        </h5>
        <b-form-checkbox
          id="switch"
          ref="switchBox"
          v-model="isChecked"
          switch
          @change="selectAllRows"
        />
      </b-col>

      <b-table
        v-bind="tableAttr"
        ref="refTable"
        class="position-relative"
        :items="fetch"
        responsive
        hover
        sort-icon-left
        :fields="tableColumnsInner"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :sort-desc.sync="sortOrder"
        :busy="busy"
        :select-mode="selectMode"
        :selectable="selectable"
        @row-selected="onRowSelected"
      >
        <template
          v-for="column in tableColumnsInner"
          #[`head(${column.key})`]
        >
          <div
            v-if="column.key === 'select' && !showSelectAllSwitchButton"
            :key="column.key"
            style="width: 10px"
          >
            <custom-checkbox
              :value="isAllSelected"
              :header="true"
              :label="$t('foundation.selectAllRows')"
              @click.native="handleAllSelectToggle"
            />
          </div>

          <span
            v-else
            :key="column.key"
            class="columnTitle"
            :style="columnTitleStyle"
          >
            <feather-icon
              v-if="getIcon(column)"
              :color="getIcon(column).color"
              :icon="getIcon(column).icon"
              size="18"
            />
            {{ column.label }}
          </span>
        </template>
        <template
          v-if="selectable && !showSelectAllSwitchButton"
          #[`cell(select)`]="{ item }"
        >
          <div style="width: 10px">
            <custom-checkbox
              :value="isSelected(item[selectKey])"
              :label="$t('foundation.selectRow')"
              @click.native="(value) => handleSelectToggle(value, item[selectKey])"
            />
          </div>
        </template>
        <template
          v-for="(index, name) in $scopedSlots"
          #[name]="data"
        >
          <slot
            :name="name"
            v-bind="data"
          />
        </template>
        <template
          v-for="column in tableColumnsWithType"
          #[`cell(${column.key})`]="{ item }"
        >
          <date-cell
            v-if="column.type === 'dateTime' && item[column.key]"
            :key="column.key"
            :value="item[column.key]"
          />
          <writer-cell
            v-if="column.type === 'writer'"
            :key="column.key"
            :value="item[column.key] != undefined ? item[column.key] : 'تعریف نشده'"
          />
          <image-cell
            v-if="column.type === 'image'"
            :key="column.key"
            :value="item[column.key]"
          />
          <text-image-cell
            v-if="column.type === 'textImage'"
            :key="column.key"
            :text="item[column.textKey]"
            :image="item[column.imageKey]"
            :link="item[column.linkKey]"
            :width="column.width || 200"
            :text-sub-key="column.textSubKey"
          />
          <truncate-text-cell
            v-if="column.type === 'truncate-text' && item[column.key]"
            :key="column.key"
            :text="item[column.key]"
            :link="!!item[column.linkKey]"
            :to="item[column.linkKey]"
            :width="column.width || 200"
            :text-sub-key="column.textSubKey"
          />
          <pill-cell
            v-if="column.type === 'pills'"
            :key="column.key"
            :items="item[column.key]"
            :width="column.width || 200"
          />
          <user-cell
            v-if="column.type === 'user'"
            :key="column.key"
            :user="item[column.key] != undefined ? item[column.key] : 'تعریف نشده'"
            :width="column.width || 200"
          />
          <manager-cell
            v-if="column.type === 'manager'"
            :key="column.key"
            :manager="item[column.key] != undefined ? item[column.key] : 'تعریف نشده'"
            :lang="column.lang"
          />
          <bold-cell
            v-if="column.type === 'bold'"
            :key="column.key"
            :text="item[column.key] != undefined ? item[column.key] : 'تعریف نشده'"
            :width="column.width || 200"
          />
          <component
            :is="column.component"
            v-if="column.type === 'custom'"
            :key="column.key"
            :value="item"
          />
        </template>
        <template #table-busy>
          <app-data-state kind="loading" />
        </template>
        <template #empty>
          <app-data-state
            v-if="loadError"
            :kind="loadErrorState"
            @retry="retry"
          />
          <app-data-state
            v-else-if="searchQuery"
            kind="filtered"
            @clear="searchQuery = ''"
          />
          <app-data-state
            v-else
            kind="empty"
          />
        </template>
      </b-table>
      <div
        v-if="pagination"
        class="mom-table-footer"
      >
        <b-row class="w-100 align-items-center">
          <b-col
            cols="12"
            sm="3"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              {{ $t("general.show") }}
              {{ dataMeta.from }}
              {{ $t("general.to") }}
              {{ dataMeta.to }}
              {{ $t("general.of") }}
              {{ dataMeta.of }}
              {{ $t("general.info") }}
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center"
          >
            <b-pagination
              v-if="total > 0"
              v-model="currentPage"
              :total-rows="total"
              :per-page="limit"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #next-text>
                <feather-icon
                  :icon="paginationIcons.next"
                  size="18"
                />
              </template>
              <template #prev-text>
                <feather-icon
                  :icon="paginationIcons.prev"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
          <b-col
            cols="12"
            sm="3"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <label>{{ $t("general.show") }}</label>
            <v-select
              v-model="limit"
              dir="rtl"
              :options="limitOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>{{ $t("general.row") }}</label>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BFormInput,
  BImg,
  BLink,
  BMedia,
  BPagination,
  BRow,
  BTable,
  BFormCheckbox,
  BFormGroup,
} from 'bootstrap-vue'
import BMediaCustom from '@/components/BMediaCustom/index.vue'
import CustomCheckbox from '@/components/CustomCheckbox/index.vue'
import AppDataState from '@/components/foundation/AppDataState.vue'
import { loadErrorKind } from '@/composable/operationFeedback'
import vSelect from 'vue-select'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import BoldCell from './Elements/BoldCell.vue'
import DateCell from './Elements/DateCell.vue'
import WriterCell from './Elements/WriterCell.vue'
import ImageCell from './Elements/ImageCell.vue'
import PillCell from './Elements/PillCell.vue'
import TextImageCell from './Elements/TextImageCell.vue'
import TruncateText from './Elements/TruncateText.vue'
import UserCell from './Elements/UserCell.vue'
import ManagerCell from './Elements/ManagerCell.vue'
import useDataTable from './useDataTable'
import { IconEnums } from './enums'

const HEADER_OPACITY = 0.6

export default {
  name: 'BDataTable',
  components: {
    BButton,
    BCard,
    BCol,
    BDropdown,
    BDropdownItem,
    BFormInput,
    BImg,
    BLink,
    BMedia,
    BMediaCustom,
    BPagination,
    BRow,
    BTable,
    BoldCell,
    CustomCheckbox,
    AppDataState,
    DateCell,
    WriterCell,
    ImageCell,
    PillCell,
    TextImageCell,
    TruncateTextCell: TruncateText,
    UserCell,
    vSelect,
    VSelectCustom,
    ManagerCell,
    BFormCheckbox,
    BFormGroup,
  },
  props: {
    fetchList: {
      type: Function,
      required: true,
    },
    tableColumns: {
      type: Array,
      required: true,
    },
    tableAttr: {
      type: Object,
      default: () => ({}),
    },
    busy: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: true,
    },
    selectKey: {
      type: String,
      default: '_id',
    },
    deleteItem: {
      type: Boolean,
      default: true,
    },
    pagination: {
      type: Boolean,
      default: true,
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    selectMode: {
      type: String,
      default: 'multi',
    },
    showSelectAllSwitchButton: {
      type: Boolean,
      default: false,
    },
    createRoute: {
      type: [Object, String],
      default: null,
    },
    createLabel: {
      type: String,
      default: '',
    },
  },
  setup() {
    const {
      currentPage,
      dataMeta,
      handleAllSelectToggle,
      handleSelectToggle,
      isAllSelected,
      isLoading,
      isSelected,
      isSortDirDesc,
      items,
      limit,
      limitOptions,
      refTable,
      refetchData,
      searchQuery,
      //      searchByTitle,
      selectList,
      sortBy,
      sortTable,
      total,
      rootCategoryOptions,
      fetchCategories,
    } = useDataTable()

    return {
      currentPage,
      dataMeta,
      handleAllSelectToggle,
      handleSelectToggle,
      isAllSelected,
      isLoading,
      isSelected,
      isSortDirDesc,
      items,
      limit,
      limitOptions,
      refTable,
      refetchData,
      searchQuery,
      // searchByTitle,
      selectList,
      sortBy,
      sortTable,
      total,
      rootCategoryOptions,
      fetchCategories,
    }
  },
  data() {
    return {
      page: 1,
      params: {},
      sortOrder: true,
      isChecked: false,
      categoryId: 'همه دسته بندی ها',
      loadError: false,
      loadErrorState: 'error',
    }
  },
  computed: {
    paginationIcons() {
      const rtl = this.$i18n.locale !== 'en'
      return rtl ? { prev: 'ChevronRightIcon', next: 'ChevronLeftIcon' } : { prev: 'ChevronLeftIcon', next: 'ChevronRightIcon' }
    },
    columnTitleStyle() {
      return {
        opacity: HEADER_OPACITY,
      }
    },
    tableColumnsWithType() {
      return this.tableColumns.filter(column => Object.prototype.hasOwnProperty.call(column, 'type'))
    },
    tableColumnsInner() {
      const newTableColumns = !this.selectable
        ? this.tableColumns
        : [{ key: 'select', sortable: false }, ...this.tableColumns]
      return newTableColumns
    },

  },
  watch: {
    selected() {
      this.searchQuery = null
    },
    currentPage() {
      const query = { ...this.$route.query, page: this.currentPage }
      this.$router.push({
        path: this.$route.path,
        query,
      })
      if (this.$route.path.includes('articles/list')) {
        this.fetch()
      }
    },
    searchQuery() {
      if (this.$route.path.includes('articles/list')) {
        this.fetch()
      }
    },
    categoryId() {
      if (this.$route.path.includes('articles/list')) {
        this.fetch()
          .then(data => {
            if (this.$route.query.page === undefined) {
              const query = { ...this.$route.query, page: 1 }
              this.$router.push({
                path: this.$route.path,
                query,
              })
            } else if (this.$route.query.page === '1' || this.$route.query.page > 1) {
              this.$router.push('/articles/list')
            } else if (this.$route.path === '/articles/list' && this.$route.query.page === undefined) {
              const query = { ...this.$route.query, page: this.currentPage }
              this.$router.push({
                path: this.$route.path,
                query,
              })
              this.items = data
              return this.items
            }
            this.currentPage = this.$route.query.page
            this.items = data
            return this.items
          })
      }
    },
  },
  created() {
    this.fetchCategories('fa', '', '', 'null')
  },
  mounted() {
    if (this.$route.query.page) {
      this.currentPage = this.$route.query.page
    }

    this.sortBy = localStorage.getItem('status')
    this.isSortDirDesc = JSON.parse(localStorage.getItem('sortOrder'))
    this.fetchCategories('fa', ' ', 'title')
  },
  methods: {
    groupDelete() {
      this.$emit('onGroupDelete', this.selectList)
    },
    async fetch() {
      this.isLoading = true
      this.isChecked = false
      if (this.$route.path.includes('momExperiences/list')) {
        this.params = {
          limit: this.limit,
          page: this.currentPage,
          sort_column: this.sortBy ? this.sortBy : 'updatedAt',
          sort_order: 'desc',
          ...this.filters,
          lang: 'fa',
        }
      } else if (this.$route.path.includes('counselings/list')) {
        this.params = {
          limit: this.limit,
          page: this.currentPage,
          sort_column: this.sortBy ? this.sortBy : 'updatedAt',
          sort_order: this.isSortDirDesc ? 'asc' : 'desc',
          ...this.filters,
          lang: 'fa',
        }
      } else if (this.$route.path.includes('articles/list')) {
        this.params = {
          limit: this.limit,
          page: this.currentPage,
          sort_column: this.sortBy,
          sort_order: this.isSortDirDesc ? 'desc' : 'asc',
          categoryId: !this.categoryId._id ? '' : this.categoryId._id,
          ...this.filters,
        }
      } else {
        this.params = {
          limit: this.limit,
          page: this.currentPage,
          sort_column: this.sortBy,
          sort_order: this.isSortDirDesc ? 'desc' : 'asc',
          ...this.filters,
        }
      }
      if (this.searchable) {
        this.params.search = this.searchQuery
      }

      this.loadError = false
      try {
        const res = await this.fetchList(this.params)
        this.total = res.total
        this.items = res.data
        localStorage.setItem('status', this.sortBy)
        localStorage.setItem('sortOrder', this.isSortDirDesc)
        this.isLoading = false
        return this.items
      } catch (error) {
        this.isLoading = false
        this.loadError = true
        this.loadErrorState = loadErrorKind(error)
        this.total = 0
        // Returning an empty page lets the table leave its busy state and show the retry panel.
        this.items = []
        return this.items
      }
    },
    retry() {
      this.loadError = false
      this.$refs.refTable.refresh()
    },
    columnSlotName(key) {
      return `#head(${key})`
    },
    getIcon({ icon, key, color }) {
      const iconObject = {}
      if (!icon && !IconEnums[key]) return null
      if (icon) {
        iconObject.icon = icon
      } else if (IconEnums[key]) {
        iconObject.icon = IconEnums[key].icon
      }
      if (color) {
        iconObject.color = color
      } else if (IconEnums[key]) {
        iconObject.color = IconEnums[key].color
      }
      return iconObject
    },
    onRowSelected(items) {
      this.$emit('selectedItems', items)
    },
    selectAllRows() {
      if (this.isChecked) this.$refs.refTable.selectAllRows()
      else this.$refs.refTable.clearSelected()
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "./index.scss";
</style>
